import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

const DISMISSED_STORAGE_KEY = "aero-passport-install-dismissed-at-v2";
const DISMISS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
const MANUAL_HINT_DELAY_MS = 12_000;

function isStandaloneDisplay() {
  if (typeof window === "undefined") return false;

  const navigatorWithStandalone = window.navigator as Navigator & {
    standalone?: boolean;
  };

  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    navigatorWithStandalone.standalone === true
  );
}

function isIosDevice() {
  if (typeof window === "undefined") return false;

  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function isAndroidDevice() {
  if (typeof window === "undefined") return false;

  return /android/i.test(window.navigator.userAgent);
}

function getBrowserName() {
  if (typeof window === "undefined") return "browser";

  const userAgent = window.navigator.userAgent;

  if (/CriOS|Chrome|Chromium/i.test(userAgent) && !/EdgiOS|Edg\/|OPR|Opera|SamsungBrowser/i.test(userAgent)) {
    return "Chrome";
  }
  if (/EdgiOS|Edg\//i.test(userAgent)) return "Edge";
  if (/FxiOS|Firefox/i.test(userAgent)) return "Firefox";
  if (/SamsungBrowser/i.test(userAgent)) return "Samsung Internet";
  if (/OPR|Opera/i.test(userAgent)) return "Opera";
  if (/Safari/i.test(userAgent) && !/Chrome|CriOS|Chromium|Android/i.test(userAgent)) return "Safari";

  return "browser";
}

function shouldShowManualInstallHint() {
  if (typeof window === "undefined") return false;
  if (Capacitor.isNativePlatform()) return false;
  if (isStandaloneDisplay()) return false;

  return true;
}

function getManualInstallInstruction(isPortuguese: boolean) {
  const browserName = getBrowserName();
  const isIos = isIosDevice();
  const isAndroid = isAndroidDevice();

  if (isPortuguese) {
    if (browserName === "Chrome" && isAndroid) {
      return "No Chrome, toque no menu ⋮ e escolha “Instalar app” ou “Adicionar à tela inicial”.";
    }

    if (browserName === "Chrome" && isIos) {
      return "No Chrome, toque no menu de compartilhamento e escolha “Adicionar à Tela de Início”.";
    }

    if (browserName === "Chrome") {
      return "No Chrome, abra o menu ⋮ e escolha “Instalar app” quando a opção aparecer.";
    }

    if (browserName === "Safari" && isIos) {
      return "No Safari, toque em Compartilhar e escolha “Adicionar à Tela de Início”.";
    }

    if (browserName === "Safari") {
      return "No Safari, use Compartilhar ou Arquivo e escolha “Adicionar ao Dock” quando disponível.";
    }

    return "Abra o menu do navegador e escolha “Instalar app” ou “Adicionar à tela inicial”.";
  }

  if (browserName === "Chrome" && isAndroid) {
    return "In Chrome, tap the ⋮ menu and choose “Install app” or “Add to Home screen”.";
  }

  if (browserName === "Chrome" && isIos) {
    return "In Chrome, open the share menu and choose “Add to Home Screen”.";
  }

  if (browserName === "Chrome") {
    return "In Chrome, open the ⋮ menu and choose “Install app” when the option appears.";
  }

  if (browserName === "Safari" && isIos) {
    return "In Safari, tap Share and choose “Add to Home Screen”.";
  }

  if (browserName === "Safari") {
    return "In Safari, use Share or File and choose “Add to Dock” when available.";
  }

  return "Open your browser menu and choose “Install app” or “Add to Home screen”.";
}

function wasRecentlyDismissed() {
  if (typeof window === "undefined") return true;

  const dismissedAt = Number(window.localStorage.getItem(DISMISSED_STORAGE_KEY) || "0");
  return Number.isFinite(dismissedAt) && Date.now() - dismissedAt < DISMISS_WINDOW_MS;
}

export function PwaInstallPrompt() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const installEventRef = useRef<BeforeInstallPromptEvent | null>(null);
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [showManualHint, setShowManualHint] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const isPortuguese = i18n.language?.toLowerCase().startsWith("pt");
  const canShowPrompt = Boolean(installEvent) || showManualHint;
  const suppressForHome = location.pathname === "/";
  const suppressForConsultant =
    location.pathname === "/app/travel-desk" || location.pathname.startsWith("/app/travel-desk/");
  const hasMobileBottomDock =
    location.pathname === "/app/dashboard" || location.pathname.startsWith("/passport/");

  useEffect(() => {
    if (typeof window === "undefined" || suppressForConsultant || Capacitor.isNativePlatform()) return;
    if (isStandaloneDisplay() || (!suppressForHome && wasRecentlyDismissed())) {
      setIsDismissed(true);
      return;
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      if (suppressForHome) return;
      const typedEvent = event as BeforeInstallPromptEvent;
      installEventRef.current = typedEvent;
      setInstallEvent(typedEvent);
      setShowManualHint(false);
      setIsDismissed(false);
    };

    const handleAppInstalled = () => {
      installEventRef.current = null;
      setInstallEvent(null);
      setShowManualHint(false);
      setIsDismissed(true);
      window.localStorage.setItem(DISMISSED_STORAGE_KEY, String(Date.now()));
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    const manualHintTimer = window.setTimeout(() => {
      if (!suppressForHome && !installEventRef.current && shouldShowManualInstallHint()) {
        setShowManualHint(true);
      }
    }, MANUAL_HINT_DELAY_MS);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
      window.clearTimeout(manualHintTimer);
    };
  }, [suppressForHome, suppressForConsultant]);

  if (Capacitor.isNativePlatform() || suppressForHome || suppressForConsultant || isDismissed || !canShowPrompt) return null;

  const manualInstallInstruction = getManualInstallInstruction(isPortuguese);

  const copy = isPortuguese
    ? {
        eyebrow: "Aero Passport app",
        title: "Adicionar à tela inicial",
        body: showManualHint && !installEvent
          ? manualInstallInstruction
          : "Instale o Aero Passport para abrir sua viagem em modo app.",
        primary: "Instalar app",
        dismiss: "Agora não",
      }
    : {
        eyebrow: "Aero Passport app",
        title: "Add to home screen",
        body: showManualHint && !installEvent
          ? manualInstallInstruction
          : "Install Aero Passport to open your trip in an app-like experience.",
        primary: "Install app",
        dismiss: "Not now",
      };

  const dismissPrompt = () => {
    setIsDismissed(true);
    window.localStorage.setItem(DISMISSED_STORAGE_KEY, String(Date.now()));
  };

  const handleInstall = async () => {
    if (!installEvent) return;

    await installEvent.prompt();
    const choice = await installEvent.userChoice;

    installEventRef.current = null;
    setInstallEvent(null);
    if (choice.outcome === "accepted" || choice.outcome === "dismissed") {
      dismissPrompt();
    }
  };

  return (
    <aside
      className={`fixed inset-x-4 z-[80] mx-auto max-w-md rounded-[28px] border border-white/15 bg-[#071326]/95 p-4 text-white shadow-[0_24px_70px_rgba(0,0,0,0.45)] backdrop-blur-xl sm:bottom-4 sm:left-auto sm:right-5 sm:mx-0 ${
        hasMobileBottomDock ? "bottom-[calc(env(safe-area-inset-bottom)+6.5rem)]" : "bottom-4"
      }`}
      aria-label={copy.title}
    >
      <div className="flex gap-3">
        <img
          src="/icons/aero-passport-icon-192.png"
          alt=""
          className="h-14 w-14 shrink-0 rounded-2xl border border-white/10 object-cover"
        />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#d8b24d]">
            {copy.eyebrow}
          </p>
          <h2 className="mt-1 text-lg font-black leading-tight">{copy.title}</h2>
          <p className="mt-1 text-sm leading-relaxed text-white/72">{copy.body}</p>
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        {installEvent && (
          <button
            type="button"
            onClick={handleInstall}
            className="flex-1 rounded-2xl bg-[#d8b24d] px-4 py-3 text-sm font-black text-[#071326] transition hover:bg-[#f0d67a]"
          >
            {copy.primary}
          </button>
        )}
        <button
          type="button"
          onClick={dismissPrompt}
          className="rounded-2xl border border-white/15 px-4 py-3 text-sm font-bold text-white/82 transition hover:bg-white/10"
        >
          {copy.dismiss}
        </button>
      </div>
    </aside>
  );
}
